/**
 * Supabase Client Configuration
 *
 * Singleton Supabase client for database and storage operations.
 * This client is shared across the application to avoid multiple instances.
 *
 * For authentication, use the provider-agnostic authProvider from './auth'.
 *
 * STORAGE STRATEGY (Feb 2026):
 * Using default localStorage for simplicity and reliability with PKCE flow.
 * Custom storage adapters were causing PKCE code_verifier issues during OAuth callbacks.
 *
 * Security considerations:
 * - PKCE flow provides strong OAuth security
 * - Modern browsers + CSP headers mitigate XSS risks
 * - Session tokens auto-expire and refresh
 * - Battle-tested by Supabase in production apps
 */

import { createClient } from '@supabase/supabase-js';
import { removeForeignSupabaseKeys } from './storageCleanup';

// Re-export helper functions from auth module (for backward compatibility)
// Note: We only re-export functions, not authProvider, to avoid circular dependency
export { getUserScenario, handleAuthError } from './auth';
export type { UserScenario } from './auth';

// Supabase configuration
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://placeholder.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'placeholder-key';

if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
  console.warn('Supabase configuration missing. Auth features will be disabled.');
}

// Before the client reads storage: drop auth keys from a previous Supabase project
// (pre-migration sessions stalled sign-in until the code exchange timed out).
// Only with a real URL — the placeholder would treat every real key as foreign.
if (import.meta.env.VITE_SUPABASE_URL) {
  try {
    const removed = removeForeignSupabaseKeys(supabaseUrl);
    if (removed.length > 0) {
      console.warn('[Supabase] Removed auth keys from a previous project:', removed);
    }
  } catch {
    // storage unavailable (private mode, blocked site data) — nothing to clean
  }
}

// Create Supabase client with standard localStorage (recommended for Vite/React SPAs)
// ✅ PKCE Flow: OAuth 2.0 with proof key for code exchange (industry standard)
// ✅ Auto Refresh: Tokens refresh automatically before expiration
// ✅ Persist Session: Sessions survive page reloads
// ✅ URL Detection: Handles OAuth callbacks automatically
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    flowType: 'pkce',               // ✅ OAuth 2.0 PKCE for secure authentication
    persistSession: true,           // ✅ Persist sessions across page loads
    autoRefreshToken: true,         // ✅ Automatically refresh tokens
    detectSessionInUrl: false,      // ✅ Disable auto-exchange: AuthCallback.tsx handles manually (prevents double-exchange race condition)
    debug: import.meta.env.DEV,     // Enable debug logs in development
  },
  global: {
    headers: {
      'x-application': 'hover-platform',
    },
  },
});

/**
 * @deprecated Use authProvider from './auth' instead
 *
 * This ensures provider-agnostic auth that can be swapped
 * for Auth0, Keycloak, Azure AD, etc.
 */
export const deprecatedSupabaseClient = supabase;

// ============================================================================
// Type Definitions for Database Tables (Migration Week 1)
// ============================================================================

export type Profile = {
  id: string;
  email: string;
  created_at: string;
  updated_at: string;
  migrated_from_beta: boolean;
  beta_user_id: number | null;
  user_prepopulated_at: string | null;
  data_migrated_at: string | null;
  ai_coach_enabled: boolean;
  ai_coach_consent_at: string | null;
  ai_coach_consent_version: string | null;
  data_export_requested_at: string | null;
  data_deletion_requested_at: string | null;
  founders_circle: boolean;
};

export type JourneyProgress = {
  id: string;
  user_id: string;
  current_step: number;
  current_substep: string | null;
  started_at: string;
  completed_at: string | null;
  last_active_at: string;

  preflight_completed: boolean;
  preflight_completed_at: string | null;

  identity_completed: boolean;
  identity_completed_at: string | null;

  life_completed: boolean;
  life_stories_count: number;
  life_completed_at: string | null;

  money_completed: boolean;
  money_decisions_count: number;
  money_completed_at: string | null;

  joy_completed: boolean;
  joy_connections_count: number;
  joy_completed_at: string | null;

  license_completed: boolean;
  license_completed_at: string | null;

  created_at: string;
  updated_at: string;
};

export type LifeStory = {
  id: string;
  user_id: string;
  journey_progress_id: string;
  story_number: number;
  situation: string | null;
  action: string | null;
  feeling: string | null;
  value: string | null;
  ai_coach_guidance: string | null;
  ai_coach_insights: any | null;
  created_at: string;
  updated_at: string;
};

export type MoneyDecision = {
  id: string;
  user_id: string;
  journey_progress_id: string;
  decision_number: number;
  decision_type: string | null;
  current_allocation: string | null;
  desired_allocation: string | null;
  why_change: string | null;
  ai_coach_guidance: string | null;
  ai_coach_insights: any | null;
  created_at: string;
  updated_at: string;
};

export type JoyConnection = {
  id: string;
  user_id: string;
  journey_progress_id: string;
  connection_name: string;
  connection_type: string;
  relationship_strength: number;
  interaction_frequency: string | null;
  value_exchange_given: string[] | null;
  value_exchange_received: string[] | null;
  shared_contexts: string[] | null;
  notes: string | null;
  source: string;

  // Week 7: Hybrid Privacy Model fields
  visibility: 'private' | 'connections_only' | 'public';
  promoted_to_community: boolean;
  community_connection_id: string | null;
  promoted_at: string | null;

  // AI Coach
  ai_coach_guidance: string | null;
  ai_coach_insights: any | null;

  // Metadata
  created_at: string;
  updated_at: string;
};

export type AICoachConversation = {
  id: string;
  user_id: string;
  journey_progress_id: string | null;
  step: string | null;
  substep: string | null;
  messages: any;
  model: string;
  tokens_used: number | null;
  user_consented: boolean;
  created_at: string;
  updated_at: string;
};